import { supabase } from '../../../config/supabase';
import html2pdf from 'html2pdf.js';
import { formatDateFR } from '../../shared/utils/helpers';
import { carDamageCompactHtml } from '../../shared/utils/carDamage';
import { escapeForHtml, escapeHtml } from '../../shared/utils/escapeHtml';
import { getActiveClauses, buildClausesHtmlFR, buildClausesHtmlAR } from './contractClauses';

/**
 * Shared contract document generator (the official "pink" rental contract).
 * Extracted from RentalsPage so the public signing page can produce the EXACT
 * same document. Actions:
 *   - 'get_contract_blob' → PDF blob of ONLY page 1 (contract) + page 2 (terms),
 *     EXCLUDING page 3 (التزام/engagement) and page 4 (NARSA déclaration).
 */
export const generateContractDoc = async (rOriginal, opts = {}) => {
  const { cars = [], isAr = false, action = 'print' } = opts;
    // Open the print window SYNCHRONOUSLY (inside the click gesture) BEFORE any
    // await — otherwise the browser's popup blocker silently kills it and the
    // button "does nothing". We write into this window after the data loads.
    let printWin = null;
    if (action === 'print' || action === 'print_rectoverso') {
      printWin = window.open('', '_blank');
    }
    // XSS: escape all agency/user-supplied data before HTML interpolation.
    const settings = escapeForHtml(opts.settings || {});
    // Contract VERSO clauses: use this agency's custom set (already escaped above
    // since it lives in `settings.contractClauses`), else the system defaults.
    const _activeClauses = getActiveClauses(settings);
    const clausesHtmlFR = buildClausesHtmlFR(_activeClauses);
    const clausesHtmlAR = buildClausesHtmlAR(_activeClauses);
    let r = rOriginal;
    try {
      const { data: freshRow } = await supabase.from('rentals').select('*').eq('id', rOriginal.id).single();
      if (freshRow) {
        r = { id: freshRow.id, ...freshRow.data };
      }
    } catch (err) {
      console.warn('Could not fetch fresh rental for contract:', err);
    }
    // XSS: escape every rental field (`damages` is escaped by its own renderer).
    r = escapeForHtml(r);

    const car = cars.find(c => c.id === r.carId);
    const carChassis = escapeHtml(car?.chassisNumber || r.carChassis || '....................');
    
    const today = new Date().toLocaleDateString('fr-FR');
    
    const phones = (settings.companyPhone || '').split(/[\/,]/).map(p => p.trim()).filter(Boolean);
    const phonesStr = phones.length > 0 ? phones.join(' / ') : '06 00 00 00 00';
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Contrat de Location - ${r.clientName || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: A4 portrait; margin: 5mm; }
  html, body { background: #ffd6e0 !important; margin: 0; padding: 0; }
  body { 
    font-family: Arial, 'Tahoma', sans-serif; 
    background: #ffd6e0; 
    color: #000;
    font-size: 12.5px;
    line-height: 1.4;
  }
  .contract {
    background: #ffd6e0;
    border: 1.5px solid #000;
    padding: 7px 10px;
    width: 100%;
    max-width: 200mm;
    margin: 0 auto;
  }
  .contract.page-3, .contract.page-4 {
    background: white !important;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 1.5px solid #000;
    padding-bottom: 4px;
    margin-bottom: 6px;
    gap: 8px;
  }
  .company-name {
    font-weight: 900;
    font-size: 20.5px;
    line-height: 1;
    letter-spacing: 0.3px;
  }
  .company-address {
    font-size: 11.5px;
    margin-top: 2px;
    line-height: 1.4;
  }
  .header-box {
    border: 1.5px solid #000;
    padding: 3px 9px;
    font-weight: 700;
    border-radius: 14px;
    font-size: 13.5px;
    background: #ffd6e0;
    white-space: nowrap;
  }
  .right-section {
    text-align: right;
  }
  .right-section .header-box {
    margin-bottom: 3px;
  }
  .papers-row {
    display: flex;
    gap: 10px;
    margin: 4px 0 6px;
    font-size: 12px;
    flex-wrap: wrap;
  }
  .checkbox-item {
    display: flex;
    align-items: center;
    gap: 3px;
    font-weight: 600;
  }
  .checkbox {
    width: 9px;
    height: 9px;
    border: 1px solid #000;
    display: inline-block;
    text-align: center;
    line-height: 8px;
    font-weight: 900;
    font-size: 9.5px;
  }
  .two-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .section-title {
    display: inline-block;
    border: 1.5px solid #000;
    padding: 1.5px 12px;
    font-weight: 900;
    font-size: 14.5px;
    border-radius: 16px;
    background: #ffd6e0;
    margin-bottom: 3px;
  }
  .field {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
    font-size: 12px;
    gap: 3px;
  }
  .field-label {
    font-weight: 700;
    white-space: nowrap;
    font-size: 12px;
  }
  .field-value {
    flex: 1;
    border-bottom: 1px dotted #000;
    min-height: 11px;
    padding: 0 3px;
    font-weight: 600;
    text-align: center;
  }
  .field-ar {
    font-weight: 700;
    direction: rtl;
    white-space: nowrap;
    margin-left: auto;
    font-size: 12px;
  }
  .right-field {
    display: flex;
    align-items: center;
    margin-bottom: 2.5px;
    gap: 4px;
  }
  .right-field-label {
    border: 1px solid #000;
    border-radius: 9px;
    padding: 1.5px 5px;
    font-weight: 700;
    font-size: 10.5px;
    min-width: 75px;
    text-align: center;
    background: #ffd6e0;
    line-height: 1.1;
  }
  .right-field-label .ar {
    display: block;
    font-size: 9.5px;
    direction: rtl;
  }
  .right-field-value {
    flex: 1;
    border-bottom: 1px dotted #000;
    min-height: 13px;
    padding: 0 4px;
    font-weight: 600;
    font-size: 12.5px;
    text-align: center;
  }
  .accessories-section {
    margin-top: 5px;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 8px;
    align-items: center;
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
    padding: 4px 0;
  }
  .accessories-label {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-weight: 900;
    font-size: 9.5px;
    text-align: center;
  }
  .car-diagram {
    text-align: center;
  }
  .car-diagram svg {
    width: 90px;
    height: 36px;
  }
  .fuel-gauge {
    text-align: center;
    font-size: 11.5px;
  }
  .fuel-gauge .label {
    font-weight: 700;
    margin-bottom: 2px;
    font-size: 10.5px;
  }
  .fuel-gauge svg {
    width: 60px !important;
    height: 28px !important;
  }
  .lavage-section {
    text-align: center;
  }
  .lavage-label {
    font-weight: 900;
    font-size: 12.5px;
    margin-bottom: 3px;
  }
  .lavage-options {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12.5px;
    font-weight: 600;
  }
  .lavage-option {
    display: flex;
    align-items: center;
    gap: 3px;
  }
  .observations {
    border: 1px solid #000;
    border-radius: 10px;
    padding: 3px 8px;
    margin: 5px 0;
    font-size: 9.5px;
    font-weight: 600;
    line-height: 1.25;
  }
  .observations-title {
    font-weight: 900;
    display: inline;
  }
  .signatures {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
    margin-top: 5px;
  }
  .sig-box {
    text-align: center;
    font-size: 12px;
    font-weight: 700;
  }
  .sig-title {
    border-top: 1px solid #000;
    padding-top: 2px;
    margin-top: 30px;
    font-size: 11.5px;
    line-height: 1.4;
  }
  .date-box {
    border: 1px solid #000;
    border-radius: 10px;
    padding: 2px 10px;
    display: inline-block;
    font-weight: 700;
    font-size: 12.5px;
  }
  .empreinte-box {
    border: 1px solid #000;
    padding: 13px 8px;
    text-align: center;
    font-size: 12.5px;
    font-weight: 700;
    direction: rtl;
  }
  .footer-terms {
    margin-top: 5px;
    padding-top: 4px;
    border-top: 1.5px solid #000;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    font-size: 10.5px;
    line-height: 1.3;
  }
  .terms-fr ul, .terms-ar ul { list-style: none; padding: 0; }
  .terms-fr li, .terms-ar li {
    margin-bottom: 2px;
    padding-left: 6px;
    position: relative;
  }
  .terms-fr li::before {
    content: "-";
    position: absolute;
    left: 0;
    font-weight: 900;
  }
  .terms-ar {
    direction: rtl;
    text-align: right;
  }
  .terms-ar li::before {
    content: "-";
    position: absolute;
    right: 0;
    font-weight: 900;
  }
  .terms-ar li {
    padding-left: 0;
    padding-right: 6px;
  }
  .no-print {
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 1000;
  }
  .no-print button {
    background: #2563eb;
    color: white;
    border: none;
    padding: 10px 18px;
    font-size: 16.5px;
    font-weight: 700;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
  @media print {
    @page { size: A4 portrait; margin: 5mm; }
    html, body { background: #ffd6e0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0 !important; padding: 0 !important; }
    .contract { 
      page-break-after: always;
      page-break-inside: avoid;
      break-after: page;
      break-inside: avoid;
      margin: 0 auto !important;
      border: 1.5px solid #000 !important;
      max-height: 287mm;
      overflow: hidden;
    }
    .contract:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .contract.page-3, .contract.page-4 { 
      background: white !important;
      border: 1.5px solid #000 !important;
    }
    .contract.page-3 > *, .contract.page-4 > * {
      background: white !important;
    }
    .no-print { display: none; }
  }
</style>
</head>
<body>

<div class="no-print">
  <button onclick="window.print()">🖨️ Imprimer</button>
</div>

<div class="contract">
  <div class="contract-body">
    <!-- HEADER -->
    <div class="header">
      <div style="display: flex; align-items: center; gap: 8px;">
        ${settings.logo ? `<img src="${settings.logo}" crossorigin="anonymous" alt="Logo" style="height: 40px; max-width: 60px; object-fit: contain;"/>` : ''}
        <div>
          <div class="company-name">${(settings.companyName || 'AUTO SERVICES').toUpperCase()}</div>
          <div class="company-address">
            ${settings.companyAddress || 'Adresse :'} <br>
            GSM: ${phonesStr}
            ${(settings.companyICE || settings.companyRC || settings.companyIF) ? `
              <br><span style="font-size: 9.5px;">
                ${settings.companyICE ? `ICE: ${settings.companyICE}` : ''}
                ${settings.companyRC ? ` • RC: ${settings.companyRC}` : ''}
                ${settings.companyIF ? ` • IF: ${settings.companyIF}` : ''}
              </span>
            ` : ''}
          </div>
        </div>
      </div>
      <div style="text-align: center;">
        <div class="header-box">CONTRAT DE LOCATION</div>
      </div>
      <div class="right-section">
        <div class="header-box">Papiers de voiture</div>
        <div class="header-box" style="direction:rtl;">كراء السيارات<br><span style="font-size:13.5px;">Location de Voitures</span></div>
      </div>
    </div>

    <!-- PAPERS CHECKBOXES -->
    <div class="papers-row">
      <div class="checkbox-item"><span class="checkbox">${r.papers?.carteGrise ? '✓' : ''}</span> Carte Grise</div>
      <div class="checkbox-item"><span class="checkbox">${r.papers?.assurance ? '✓' : ''}</span> Assurance</div>
      <div class="checkbox-item"><span class="checkbox">${r.papers?.vignette ? '✓' : ''}</span> Vignette</div>
      <div class="checkbox-item"><span class="checkbox">${r.papers?.visiteTechnique ? '✓' : ''}</span> Visite Technique</div>
      <div class="checkbox-item"><span class="checkbox">${r.papers?.autorisation ? '✓' : ''}</span> Autorisation</div>
      <div class="checkbox-item"><span class="checkbox">${r.papers?.contrat !== false ? '✓' : ''}</span> Contrat</div>
    </div>

    <!-- LOCATAIRE AND VEHICLE DETAILS -->
    <div class="two-cols">
      <!-- LOCATAIRE -->
      <div>
        <div class="section-title">LOCATAIRE</div>
        <div class="field">
          <span class="field-label">Nom et Prénom:</span>
          <span class="field-value">${r.clientName || ''}</span>
          <span class="field-ar">الإسم و النسب</span>
        </div>
        <div class="field">
          <span class="field-label">Nationalité:</span>
          <span class="field-value">${r.clientNationality || 'Marocaine'}</span>
          <span class="field-ar">الجنسية</span>
        </div>
        <div class="field">
          <span class="field-label">Date de Naissance:</span>
          <span class="field-value">${r.clientBirthDate || ''}</span>
          <span class="field-ar">تاريخ الإزدياد</span>
        </div>
        <div class="field">
          <span class="field-label">Profession:</span>
          <span class="field-value">${r.clientProfession || ''}</span>
          <span class="field-ar">المهنة</span>
        </div>
        <div class="field">
          <span class="field-label">Passport N°:</span>
          <span class="field-value">${r.clientPassport || ''}</span>
          <span class="field-ar">رقم جواز السفر</span>
        </div>
        <div class="field">
          <span class="field-label">Délivré à:</span>
          <span class="field-value">${r.clientPassportPlace || ''}</span>
          <span class="field-ar">صدر في</span>
        </div>
        <div class="field">
          <span class="field-label">CIN:</span>
          <span class="field-value">${r.clientCin || ''}</span>
          <span class="field-ar">رقم ب ت و</span>
        </div>
        <div class="field">
          <span class="field-label">Permis de conduite N°:</span>
          <span class="field-value">${r.clientPermis || ''}</span>
          <span class="field-ar">رقم رخصة السياقة</span>
        </div>
        <div class="field">
          <span class="field-label">Délivré le:</span>
          <span class="field-value">${r.clientPermisDate || ''}</span>
          <span class="field-ar">صدر في</span>
        </div>
        <div class="field">
          <span class="field-label">Adresse au Maroc:</span>
          <span class="field-value">${r.clientAddress || ''}</span>
          <span class="field-ar">عنوان المغرب</span>
        </div>
        <div class="field">
          <span class="field-label">Autre Adresse:</span>
          <span class="field-value">${r.clientAddress2 || ''}</span>
          <span class="field-ar">عنوان آخر</span>
        </div>
        <div class="field">
          <span class="field-label">Téléphone:</span>
          <span class="field-value">${r.clientPhone2 || ''}</span>
          <span class="field-ar">الهاتف الثابت</span>
        </div>
        <div class="field">
          <span class="field-label">GSM:</span>
          <span class="field-value">${r.clientPhone || ''}</span>
          <span class="field-ar">الهاتف النقال</span>
        </div>

        <div class="section-title" style="margin-top: 5px;">Autre Conducteur</div>
        ${(r.hasDriver2 && (r.driver2Name || r.driver2Cin || r.driver2Permis)) ? `
        <div class="field">
          <span class="field-label">Nom et Prénom:</span>
          <span class="field-value">${r.driver2Name || ''}</span>
          <span class="field-ar">الإسم و النسب</span>
        </div>
        <div class="field">
          <span class="field-label">Permis N°:</span>
          <span class="field-value">${r.driver2Permis || ''}</span>
          <span class="field-ar">رقم رخصة السياقة</span>
        </div>
        <div class="field">
          <span class="field-label">Délivré le:</span>
          <span class="field-value">${r.driver2PermisDate || ''}</span>
          <span class="field-ar">صدر في</span>
        </div>
        <div class="field">
          <span class="field-label">CIN N°:</span>
          <span class="field-value">${r.driver2Cin || ''}</span>
          <span class="field-ar">رقم ب ت و</span>
        </div>
        ` : `
        <div class="field" style="opacity: 0.7;">
          <span class="field-value" style="border-bottom: none; font-style: italic; color: #555; text-align: center;">
            Aucun deuxième chauffeur — لا يوجد سائق إضافي
          </span>
        </div>
        `}
      </div>

      <!-- VEHICLE INFO -->
      <div>
        <div class="right-field">
          <div class="right-field-label">Marque de véhicule<span class="ar">نوع السيارة</span></div>
          <span class="right-field-value">${(r.carName || '').replace(/\s*\(.*\)\s*$/, '')}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Immatriculation<span class="ar">رقم اللوحة</span></div>
          <span class="right-field-value">${r.carPlate || ''}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Date de Départ<span class="ar">تاريخ المغادرة</span></div>
          <span class="right-field-value">${formatDateFR(r.startDate) || ''}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Date de Retour<span class="ar">تاريخ العودة</span></div>
          <span class="right-field-value">${r.isOpenContract ? '⏳ Contrat ouvert' : (formatDateFR(r.endDate) || '')}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Prolongation<span class="ar">التمديد</span></div>
          <span class="right-field-value">${r.prolongation || ''}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Kilométrage de départ<span class="ar">عدد الكلمترات عند المغادرة</span></div>
          <span class="right-field-value">${r.kmStart || ''}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Kilométrage de Retour<span class="ar">عدد الكلمترات عند العودة</span></div>
          <span class="right-field-value">${r.kmEnd || ''}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Nombre de jour<span class="ar">عدد الأيام</span></div>
          <span class="right-field-value">${r.days || 0}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Heure de Sortie<span class="ar">ساعة التسليم</span></div>
          <span class="right-field-value">${r.exitTime || ''}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Montant<span class="ar">الثمن</span></div>
          <span class="right-field-value"><strong>${r.totalAmount || 0} ${settings.currency}</strong></span>
        </div>
        <div class="right-field">
          <div class="right-field-label">M. De Réglement<span class="ar">كيفية الأداء</span></div>
          <span class="right-field-value">${r.paymentMethod || 'Espèces'}</span>
        </div>
        <div class="right-field">
          <div class="right-field-label">Reste<span class="ar">الباقي</span></div>
          <span class="right-field-value">${(r.totalAmount || 0) - (r.deposit || 0)} ${settings.currency}</span>
        </div>

        <div class="accessories-section">
          <div class="accessories-label">ACCESSOIRES</div>
          <div class="car-diagram">
            <div class="fuel-gauge">
              <div class="label">Niveau du Carburant</div>
              ${(() => {
                const fuelMap = {
                  'empty':          { angle: -75, label: 'Vide',    color: '#dc2626' },
                  'quarter':        { angle: -38, label: '1/4',     color: '#ea580c' },
                  'half':           { angle:   0, label: '1/2',     color: '#ca8a04' },
                  'three_quarters': { angle:  38, label: '3/4',     color: '#16a34a' },
                  'full':           { angle:  75, label: 'Plein',   color: '#15803d' }
                };
                const fuel = fuelMap[r.fuelLevel || 'full'] || fuelMap.full;
                const rad = (fuel.angle - 90) * Math.PI / 180;
                const endX = 40 + Math.cos(rad) * 22;
                const endY = 33 + Math.sin(rad) * 22;
                return `
                  <svg viewBox="0 0 80 40" style="width:60px; height:28px;">
                    <path d="M 5 35 A 35 35 0 0 1 75 35" fill="none" stroke="#000" stroke-width="2"/>
                    <line x1="10" y1="32" x2="14" y2="28" stroke="#000" stroke-width="1.5"/>
                    <line x1="40" y1="5" x2="40" y2="10" stroke="#000" stroke-width="1.5"/>
                    <line x1="70" y1="32" x2="66" y2="28" stroke="#000" stroke-width="1.5"/>
                    <text x="6" y="38" font-size="8" font-weight="bold">E</text>
                    <text x="72" y="38" font-size="8" font-weight="bold">F</text>
                    <line x1="40" y1="33" x2="${endX.toFixed(1)}" y2="${endY.toFixed(1)}" stroke="${fuel.color}" stroke-width="3" stroke-linecap="round"/>
                    <circle cx="40" cy="33" r="3" fill="#000"/>
                  </svg>
                  <div style="font-size: 11.5px; font-weight: 900; color: ${fuel.color}; margin-top: 1px;">${fuel.label}</div>
                `;
              })()}
            </div>
          </div>
          <div class="lavage-section">
            <div class="lavage-label">LAVAGE</div>
            <div class="lavage-options">
              <div class="lavage-option"><span class="checkbox">${r.lavage === 'oui' ? '✓' : ''}</span> OUI</div>
              <div class="lavage-option"><span class="checkbox">${r.lavage === 'non' ? '✓' : ''}</span> NON</div>
            </div>
          </div>
        </div>

        <div class="observations">
          <span class="observations-title">Observations:</span>
          Le conducteur est responsable du véhicule en cas d'accident.
          ${r.notes ? '<br>' + r.notes : ''}
        </div>
        ${carDamageCompactHtml(r.damages, { isAr })}
      </div>
    </div>
  </div>

  <div class="contract-footer">
    <div class="signatures">
      <div class="sig-box">
        <div class="date-box">Date: ${today}</div>
        <div style="height: 50px; display: flex; align-items: center; justify-content: center; margin-top: 4px;">
          ${settings.companyStamp ? `<img src="${settings.companyStamp}" crossorigin="anonymous" alt="Cachet" style="max-width: 100%; max-height: 48px; object-fit: contain;"/>` : '&nbsp;'}
        </div>
        <div class="sig-title">Cachet et Signature responsable de la<br>Sté ${(settings.companyName || 'AUTO SERVICES').toUpperCase()}</div>
      </div>
      <div class="sig-box">
        <div class="empreinte-box" style="margin-top: 0;">بصمة المكتري</div>
        <div class="sig-title" style="border-top: none; margin-top: 6px;">&nbsp;</div>
      </div>
      <div class="sig-box">
        <div style="height: 60px; display: flex; align-items: center; justify-content: center;">
          ${r.clientSignature ? `<img src="${r.clientSignature}" crossorigin="anonymous" alt="Signature" style="max-width: 100%; max-height: 55px; object-fit: contain;"/>` : '&nbsp;'}
        </div>
        <div class="sig-title">Signature du Client${r.signedAt ? '<br><span style="font-weight: 500; font-size: 9.5px;">Signé le: ' + new Date(r.signedAt).toLocaleDateString('fr-FR') + '</span>' : ''}</div>
      </div>
    </div>

    <div class="footer-terms">
      <div class="terms-fr">
        <ul>
          <li>J'ai lu et j'accepte les formes mentionnes au recto et verso du présent contrat et du carnet de bord.</li>
          <li>Sté ${(settings.companyName || 'AUTO SERVICES').toUpperCase()} se réserve le plein droit de récupérer la voiture à tous moment sans justif ni indemnité.</li>
        </ul>
      </div>
      <div class="terms-ar">
        <ul>
          <li>لقد قرأت جميع البنود المدونة خلف هذا العقد المكتوبة بالفرنسية وقبلت بها والتحمل مسؤولية السيارة خلال كامل مدة هذا العقد.</li>
          <li>شركة ${(settings.companyName || 'AUTO SERVICES').toUpperCase()} لها كامل الصلاحية في استرجاع سيارتها أي وقت وذلك دون تعويض هذا العقد لاغيا في حالة انتهاء التاريخ المدون أعلاه.</li>
        </ul>
      </div>
    </div>
  </div>
</div>

<div class="contract page-2">
  <div style="border-bottom: 1.5px solid #000; padding-bottom: 4px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;">
    <div style="font-weight: 900; font-size: 16.5px;">${(settings.companyName || 'AUTO SERVICES').toUpperCase()}</div>
    <div style="text-align: center;">
      <div class="header-box" style="font-size: 14.5px; padding: 3px 14px;">CONDITIONS GÉNÉRALES / الشروط العامة</div>
    </div>
    <div style="font-weight: 700; font-size: 13.5px; direction: rtl;">شروط عقد الكراء</div>
  </div>

  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
    <div style="font-size: 10.5px; line-height: 1.4;">
      <div style="font-weight: 900; font-size: 12.5px; text-decoration: underline; margin-bottom: 4px;">CONDITIONS GÉNÉRALES DE LOCATION</div>
      ${clausesHtmlFR}
    </div>

    <div style="font-size: 11.5px; line-height: 1.5; direction: rtl; text-align: right;">
      <div style="font-weight: 900; font-size: 13.5px; text-decoration: underline; margin-bottom: 4px;">الشروط العامة للكراء</div>
      ${clausesHtmlAR}
    </div>
  </div>

  <div style="margin-top: 10px; padding-top: 6px; border-top: 1.5px solid #000;">
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; font-size: 11.5px; line-height: 1.4;">
      <div>
        <p style="font-weight: 900; margin-bottom: 4px;">Le locataire reconnait avoir pris connaissance et accepté toutes les conditions générales et particulières du présent contrat de location.</p>
        <p style="font-style: italic; color: #555;">Lu et approuvé</p>
      </div>
      <div style="direction: rtl; text-align: right;">
        <p style="font-weight: 900; margin-bottom: 4px;">يقر المكتري أنه قد اطلع على جميع الشروط العامة والخاصة لهذا العقد وقبل بها بدون تحفظ.</p>
        <p style="font-style: italic; color: #555;">قُرئ وصُودق عليه</p>
      </div>

    </div>
    <div style="text-align: center; margin-top: 8px; font-size: 11.5px; color: #444; font-style: italic;">
      ${(settings.companyName || 'AUTO SERVICES').toUpperCase()} — ${settings.companyAddress || ''} — GSM: ${phonesStr}
    </div>
  </div>
</div>

<div class="contract page-3" style="background: white !important; direction: rtl;">
  <div style="background: white; padding: 20px 30px; font-family: 'Arial', sans-serif; color: #000;">
    <h1 style="text-align: center; font-size: 22px; font-weight: 900; margin-bottom: 25px; text-decoration: underline;">التزام بتحمل مسؤولية السيارة</h1>
    <h2 style="text-align: center; font-size: 20px; font-weight: 900; margin: 25px 0;">التزام</h2>
    <div style="font-size: 16.5px; line-height: 2.2; text-align: justify; margin-top: 40px;">
      <p style="margin-bottom: 12px;">أنا الموقع أسفله</p>
      <p style="margin-bottom: 12px;">
        السيد(ة) : <span style="display: inline-block; min-width: 250px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${r.clientName || '............................'}</span> الجنسية : <span style="display: inline-block; min-width: 120px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${r.clientNationality || 'مغربية'}</span>
      </p>
      <p style="margin-bottom: 12px;">
        الحامل للبطاقة التعريف الوطنية رقم : <span style="display: inline-block; min-width: 180px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${r.clientCin || '....................'}</span> الساكن : <span style="display: inline-block; min-width: 200px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${r.clientAddress || '....................'}</span>
      </p>
      <p style="margin: 20px 0 12px; text-align: justify;">
        بمقتضى هذا الالتزام، وتحت جميع الضمانات الفعلية والقانونية في أوسع نطاقها و مفهومها العام الجاري بها العمل و السارية المفعول و الجاري بها حسب قانون الإلتزامات و العقود أشهد و أصرح و التزم بأني المسؤول الوحيد عن السيارة ابتداء من توقيع هذا الالتزام ...
      </p>
      <p style="margin-bottom: 12px;">
        السيارة علامة : <span style="display: inline-block; min-width: 180px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${r.carBrand || r.carName || '....................'}</span> المسجلة تحت رقم : <span style="display: inline-block; min-width: 150px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${r.carPlate || '....................'}</span>
      </p>
      <p style="margin-bottom: 12px;">
        رقم الإطار الحديدي : <span style="display: inline-block; min-width: 250px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${carChassis}</span>
      </p>
      <p style="margin: 20px 0 12px; text-align: justify;">
        و أني المسؤول الوحيد و الأوحد في أداء الرسوم و كل ما يتعلق بها بما في ذلك الحوادث و الدعائر و مخالفات المرور التي تتعرض لها السيارة السالفة الذكر، و أني المسؤول عنها و المكلف بها ابتداء من تاريخ الإمضاء والمصادقة وبه التزم و أوقع أسفله.
      </p>
      <p style="margin: 35px 0 12px;">
        حرر ب : <span style="display: inline-block; min-width: 200px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${settings.companyCity || 'العيون'}</span> &nbsp;&nbsp;&nbsp; في : <span style="display: inline-block; min-width: 120px; border-bottom: 1px solid #000; padding: 0 8px; font-weight: 700;">${today}</span>
      </p>
      <p style="margin-top: 40px;">الإمضاء :</p>
      <div style="margin-top: 20px; height: 80px; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center;">
        ${r.clientSignature ? `<img src="${r.clientSignature}" crossorigin="anonymous" style="max-height: 70px; max-width: 200px; object-fit: contain;"/>` : '&nbsp;'}
      </div>
    </div>
    <div style="text-align: center; margin-top: 30px; font-size: 12.5px; color: #555; font-style: italic; padding-top: 8px; border-top: 1px solid #ccc;">
      ${(settings.companyName || 'AUTO SERVICES').toUpperCase()} — ${settings.companyAddress || ''} — ${phonesStr}
    </div>
  </div>
</div>

<div class="contract page-4" style="background: white !important; direction: ltr; font-family: Arial, sans-serif; padding: 6mm 8mm; font-size: 12.5px; line-height: 1.3;">
  <div style="display: flex; align-items: center; justify-content: space-between; padding-bottom: 4px; margin-bottom: 4px;">
    <div style="font-size: 11.5px; line-height: 1.1;">
      <div style="font-weight: 900; font-size: 16.5px; color: #1e40af;">NARSA</div>
      <div style="color: #666; font-size: 9.5px;">الوكالة الوطنية للسلامة الطرقية</div>
      <div style="color: #666; font-size: 9.5px;">NATIONAL ROAD SAFETY AGENCY</div>
    </div>
    <div style="text-align: center; flex: 1;">
      <div style="font-weight: 900; font-size: 12px; direction: rtl;">التصريح بمرتكب المخالفة</div>
      <div style="font-weight: 900; font-size: 16.5px; margin-top: 2px;">Déclaration de l'auteur de l'infraction</div>
    </div>
    <div style="width: 60px;"></div>
  </div>

  <div style="display: inline-block; background: #d6e6f5; padding: 2px 10px; border-radius: 2px; font-weight: bold; font-size: 12.5px; margin-bottom: 4px;">Réf. : DEC</div>

  <div style="display: flex; align-items: center; padding: 3px 0; border-bottom: 1px solid #aaa; gap: 4px; font-size: 12px; margin-bottom: 4px;">
    <div style="flex: 1;">
      N° de l'avis de contravention<br>
      <span style="font-style: italic; font-size: 10.5px;">(Copie à joindre au présent formulaire)</span>
    </div>
    <div style="display: flex; gap: 1px;">
      ${[1,2,3,4,5,6,7].map(()=>'<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid #333; text-align: center; line-height: 14px; font-size: 13.5px; font-weight: bold;"></span>').join('')}
      <span style="display: inline-block; padding: 0 4px; height: 14px; border: 1px solid #333; line-height: 14px; font-size: 12.5px; font-weight: bold; background: #f0f0f0; min-width: 22px; text-align: center;">R F</span>
      ${[1,2,3,4,5].map(()=>'<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid #333;"></span>').join('')}
    </div>
    <div style="flex: 1; text-align: right; direction: rtl;">
      رقم الإشعار بالمخالفة<br>
      <span style="font-style: italic; font-size: 10.5px;">(إرفاق نسخة من الإشعار بهذا المطبوع)</span>
    </div>
  </div>

  <div style="padding: 4px 0; font-size: 11.5px; line-height: 1.35; display: flex; gap: 6px;">
    <div style="flex: 1;">
      <strong>Je soussigné(e):</strong>
      <ul style="margin: 1px 0 0 12px; padding: 0; list-style-type: disc;">
        <li>Personne physique</li>
        <li>Représentant légal de la personne morale ou de l'agence de location de voitures sans chauffeur en particulier</li>
        <li>Personne habilitée en cas des administrations publiques, aux établissements et entreprise publics et aux collectivités locales</li>
      </ul>
    </div>
    <div style="flex: 1; direction: rtl; text-align: right;">
      <strong>أنا الممضي(ة) أسفله:</strong>
      <ul style="margin: 1px 12px 0 0; padding: 0; list-style-type: disc; list-style-position: inside;">
        <li>شخص ذاتي</li>
        <li>الممثل القانوني في حالة الأشخاص المعنويين عامة أو وكالات كراء السيارات بدون سائق على وجه الخصوص</li>
        <li>شخص له الصلاحية القانونية إذا تعلق الأمر بالإدارات العمومية، بالمؤسسات والشركات العمومية والجماعات المحلية</li>
      </ul>
    </div>
  </div>

  ${[
    ['Nom', settings.ownerLastName || settings.companyName || '', 'الاسم العائلي'],
    ['Prénom (s)', settings.ownerFirstName || '', 'الاسم الشخصي'],
    ['Fonction', settings.ownerFunction || 'Gérant', 'المهنة'],
    ['N° de GSM', phonesStr || settings.companyPhone || '', 'رقم الهاتف النقال'],
    ['Adresse mail', settings.companyEmail || '', 'عنوان البريد الإلكتروني'],
    ['Raison sociale', settings.companyName || '', 'اسم الشركة أو الإدارة'],
    ['Adresse', settings.companyAddress || '', 'العنوان']
  ].map(([fr, val, ar]) => `
    <div style="display: flex; border-bottom: 1px solid #999; min-height: 18px;">
      <div style="width: 26%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999; display: flex; align-items: center;">${fr}</div>
      <div style="flex: 1; padding: 3px 8px; font-family: 'Courier New', monospace; font-size: 12.5px; font-weight: bold; display: flex; align-items: center;">${val}</div>
      <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999; display: flex; align-items: center; justify-content: flex-end;">${ar}</div>
    </div>
  `).join('')}

  <div style="display: flex; border-bottom: 1px solid #999; min-height: 24px;">
    <div style="width: 14%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999; display: flex; align-items: center;">Ville</div>
    <div style="flex: 1; padding: 3px 8px; font-family: monospace; font-size: 12.5px; font-weight: bold; border-right: 1px solid #999; display: flex; align-items: center;">${settings.companyCity || ''}</div>
    <div style="width: 14%; padding: 3px 6px; font-size: 12px; background: #f7f7f7; border-right: 1px solid #999; display: flex; align-items: center;">Code postal</div>
    <div style="width: 18%; padding: 3px 8px; font-family: monospace; font-size: 12.5px; font-weight: bold; display: flex; align-items: center;"></div>
    <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999; display: flex; align-items: center; justify-content: flex-end;">الرمز البريدي&nbsp;المدينة</div>
  </div>

  <div style="padding: 5px 0; font-size: 12px; line-height: 1.5; border-bottom: 1px solid #999; display: flex; gap: 10px; align-items: center;">
    <div style="flex: 1;">
      <strong>Déclare que le véhicule, immatriculé sous le numéro</strong>
      <div style="display: inline-flex; gap: 1px; margin: 0 4px;">
        ${(r.carPlate || '').split('').slice(0, 12).map(c => `<span style="display: inline-block; width: 13px; height: 14px; border: 1px solid #333; text-align: center; line-height: 14px; font-size: 13.5px; font-weight: bold;">${c}</span>`).join('')}
        ${Array((12 - (r.carPlate || '').length > 0) ? 12 - (r.carPlate || '').length : 0).fill(0).map(() => '<span style="display: inline-block; width: 13px; height: 14px; border: 1px solid #333;"></span>').join('')}
      </div>
      <br>
      objet de l'avis de contravention sus indiqué :<br>
      était conduit, au moment de la constatation de l'infraction by :
    </div>
    <div style="flex: 1; direction: rtl; text-align: right;">
      <strong>أصرح أن المركبة المسجلة تحت رقم</strong><br>
      موضوع الإشعار بالمخالفة المشار إليها<br>
      أعلاه، كانت مساقة وقت معاينة المخالفة من طرف :
    </div>
  </div>

  <div style="background: #cfd9e8; padding: 3px 8px; font-size: 12.5px; font-weight: bold; border-top: 1.5px solid #4a6fa5; border-bottom: 1px solid #4a6fa5; display: flex; justify-content: space-between;">
    <span>Personne physique</span>
    <span style="direction: rtl;">شخص ذاتي</span>
  </div>

  ${[
    ['Nom', (r.clientName || '').split(' ')[0] || '', 'الاسم العائلي'],
    ['Prénom (s)', (r.clientName || '').split(' ').slice(1).join(' '), 'الاسم الشخصي'],
    ['Adresse', r.clientAddress || '', 'العنوان']
  ].map(([fr, val, ar]) => `
    <div style="display: flex; border-bottom: 1px solid #999; min-height: 18px;">
      <div style="width: 26%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999; display: flex; align-items: center;">${fr}</div>
      <div style="flex: 1; padding: 3px 8px; font-family: monospace; font-size: 12.5px; font-weight: bold; display: flex; align-items: center;">${val}</div>
      <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999; display: flex; align-items: center; justify-content: flex-end;">${ar}</div>
    </div>
  `).join('')}

  <div style="display: flex; border-bottom: 1px solid #999; min-height: 22px;">
    <div style="width: 14%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999; display: flex; align-items: center;">Ville</div>
    <div style="flex: 1; padding: 3px 8px; font-family: monospace; font-size: 12.5px; font-weight: bold; border-right: 1px solid #999;">${r.clientCity || ''}</div>
    <div style="width: 14%; padding: 3px 6px; font-size: 12px; background: #f7f7f7; border-right: 1px solid #999;">Code postal</div>
    <div style="width: 18%; padding: 3px 8px; font-family: monospace;"></div>
    <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999;">الرمز البريدي&nbsp;المدينة</div>
  </div>

  <div style="display: flex; border-bottom: 1px solid #999; min-height: 22px;">
    <div style="width: 26%; padding: 3px 6px; font-size: 11.5px; border-right: 1px solid #999;">N° de la C.N.I.E ou de la carte<br>séjour <em>(joindre une copie)</em></div>
    <div style="flex: 1; padding: 3px 8px; display: flex; align-items: center; gap: 1px;">
      ${(r.clientCin || '').split('').slice(0, 10).map(c => `<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid #333; text-align: center; line-height: 14px; font-size: 13.5px; font-weight: bold;">${c}</span>`).join('')}
      ${Array((10 - (r.clientCin || '').length > 0) ? 10 - (r.clientCin || '').length : 0).fill(0).map(() => '<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid #333;"></span>').join('')}
    </div>
    <div style="width: 26%; padding: 3px 6px; font-size: 11.5px; direction: rtl; text-align: right; border-left: 1px solid #999;">رقم البطاقة الوطنية للتعريف الإلكترونية أو بطاقة<br>الإقامة (إرفاق نسخة)</div>
  </div>

  <div style="display: flex; border-bottom: 1px solid #999; min-height: 18px;">
    <div style="width: 26%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999;">N° de permis de conduire <em>(joindre une copie)</em></div>
    <div style="flex: 1; padding: 3px 8px; font-family: monospace; font-size: 12.5px; font-weight: bold; display: flex; align-items: center;">${r.clientPermis || ''}</div>
    <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999;">رقم رخصة السياقة (إرفاق نسخة)</div>
  </div>

  <div style="background: #cfd9e8; padding: 3px 8px; font-size: 12px; border-top: 1.5px solid #4a6fa5; border-bottom: 1px solid #4a6fa5; display: flex; justify-content: space-between;">
    <div>
      <strong>Personne morale</strong><br>
      <span style="font-size: 10.5px; font-weight: normal;">Réservé aux agences de location de voitures sans chauffeur</span>
    </div>
    <div style="direction: rtl; text-align: right;">
      <strong>شخص معنوي</strong><br>
      <span style="font-size: 10.5px; font-weight: normal;">خاص بوكالات كراء السيارات بدون سائق في حالة الكراء لشخص معنوي</span>
    </div>
  </div>

  ${[
    ['Raison sociale', '', 'اسم الشركة'],
    ['Adresse', '', 'العنوان']
  ].map(([fr, val, ar]) => `
    <div style="display: flex; border-bottom: 1px solid #999; min-height: 18px;">
      <div style="width: 26%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999;">${fr}</div>
      <div style="flex: 1; padding: 3px 8px;"></div>
      <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999;">${ar}</div>
    </div>
  `).join('')}

  <div style="display: flex; border-bottom: 1px solid #999; min-height: 22px;">
    <div style="width: 14%; padding: 3px 6px; font-size: 12px; border-right: 1px solid #999;">Ville</div>
    <div style="flex: 1; padding: 3px 8px; border-right: 1px solid #999;"></div>
    <div style="width: 14%; padding: 3px 6px; font-size: 12px; background: #f7f7f7; border-right: 1px solid #999;">Code postal</div>
    <div style="width: 18%; padding: 3px 8px;"></div>
    <div style="width: 26%; padding: 3px 6px; font-size: 12px; direction: rtl; text-align: right; border-left: 1px solid #999;">الرمز البريدي&nbsp;المدينة</div>
  </div>

  <div style="display: flex; gap: 12px; padding: 5px 4px; font-size: 12.5px; border-bottom: 1px solid #999; align-items: center;">
    <div style="display: flex; gap: 4px; align-items: center;"><strong>Fait à :</strong><span style="border-bottom: 1px solid #333; padding: 0 6px; min-width: 70px; font-family: monospace; font-weight: bold;">${settings.companyCity || ''}</span></div>
    <div style="display: flex; gap: 4px; align-items: center;"><strong>Le :</strong><span style="border-bottom: 1px solid #333; padding: 0 6px; min-width: 70px; font-family: monospace; font-weight: bold;">${today}</span></div>
    <div style="display: flex; gap: 4px; align-items: center; margin-left: auto; direction: rtl;"><strong>: في</strong><span style="border-bottom: 1px solid #333; padding: 0 6px; min-width: 70px; font-family: monospace; font-weight: bold;">${today}</span></div>
    <div style="display: flex; gap: 4px; align-items: center; direction: rtl;"><strong>: حرر ب</strong><span style="border-bottom: 1px solid #333; padding: 0 6px; min-width: 70px; font-family: monospace; font-weight: bold;">${settings.companyCity || ''}</span></div>
  </div>

  <div style="display: flex; gap: 4px; margin-top: 4px; padding: 5px; border: 1px solid #999; align-items: center; min-height: 60px;">
    <div style="flex: 1; font-size: 12px;">
      <strong>Signature de la personne physique déclarant ou signature et cachet de la personne morale ou l'Administration</strong>
      <div style="direction: rtl; text-align: right; margin-top: 3px;"><strong>إمضاء الشخص الذاتي المصرح أو إمضاء وخاتم الشخص المعنوي أو الإدارة</strong></div>
    </div>
    <div style="flex: 1; text-align: center;">
      ${settings.companyStamp ? `<img src="${settings.companyStamp}" crossorigin="anonymous" alt="Cachet" style="max-height: 55px; max-width: 90%;"/>` : '<div style="height: 55px;"></div>'}
    </div>
  </div>

  <div style="display: flex; gap: 4px; margin-top: 3px; padding: 5px; border: 1px solid #999; align-items: center; min-height: 60px;">
    <div style="flex: 1; font-size: 12px;">
      <strong>Signature du déclaré</strong>
      <div style="font-style: italic; font-size: 10.5px;">(En cas de déclaration d'une personne physique)</div>
      <div style="direction: rtl; text-align: right; margin-top: 3px;">
        <strong>إمضاء المصرح به</strong>
        <div style="font-style: italic; font-size: 10.5px;">(في حالة تصريح الأشخاص الذاتيين)</div>
      </div>
    </div>
    <div style="flex: 1; text-align: center;">
      ${r.clientSignature ? `<img src="${r.clientSignature}" crossorigin="anonymous" alt="Signature" style="max-height: 55px; max-width: 90%;"/>` : '<div style="height: 55px;"></div>'}
    </div>
  </div>

  <div style="margin-top: 4px; padding: 4px; background: #f5f5f5; border: 1px solid #999; font-size: 10.5px; line-height: 1.3; display: flex; gap: 6px;">
    <div style="flex: 1;">
      Ce formulaire, dûment renseigné et signé, assorti d'une copie de l'avis de contravention objet de la déclaration et des justificatifs associés, doit être déposé, dans un délai de <strong>30 jours</strong> à compter de la date de notification de l'avis de contravention, auprès des services extérieurs de l'Agence Nationale de la Sécurité Routière.
    </div>
    <div style="flex: 1; direction: rtl; text-align: right;">
      يجب تعبئة النموذج بدقة وتوقيعه وإرفاقه بنسخة من الإشعار بالمخالفة موضوع التصريح وكذا بالوثائق المبررة وإيداعه مباشرة، داخل أجل <strong>30 يوما</strong> ابتداء من تاريخ الإشعار بالمخالفة، بإحدى المصالح الخارجية للوكالة الوطنية للسلامة الطرقية.
    </div>
  </div>

  <div style="margin-top: 4px; padding: 3px; border-top: 1px solid #999; font-size: 10.5px; text-align: center; font-style: italic;">
    <div style="direction: rtl;">ملحوظة : كل من أدلى بمعلومات غير صحيحة أو بوثائق مزيفة أو ساعد على ذلك سيعاقب طبقا للقوانين الجاري بها العمل.</div>
    <div><strong>NB :</strong> Toute personne ayant fourni de faux renseignements ou documents falsifiés ou ayant aidé pour le faire sera puni conformément aux lois en vigueur.</div>
  </div>
</div>

${(action === 'download_pdf' || action === 'get_pdf_blob') ? '' : `
<script>
  window.onload = () => {
    setTimeout(() => window.print(), 500);
  };
</script>
`}
</body>
</html>
    `;

    if (action === 'get_contract_html' || action === 'get_commitment_html' || action === 'get_narsa_html') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // Clean up print buttons
      const noPrint = doc.querySelector('.no-print');
      if (noPrint) noPrint.remove();

      if (action === 'get_contract_html') {
        const p3 = doc.querySelector('.page-3');
        if (p3) p3.remove();
        const p4 = doc.querySelector('.page-4');
        if (p4) p4.remove();
      } else if (action === 'get_commitment_html') {
        const p1 = doc.querySelector('.contract:not(.page-2):not(.page-3):not(.page-4)');
        if (p1) p1.remove();
        const p2 = doc.querySelector('.page-2');
        if (p2) p2.remove();
        const p4 = doc.querySelector('.page-4');
        if (p4) p4.remove();
      } else if (action === 'get_narsa_html') {
        const p1 = doc.querySelector('.contract:not(.page-2):not(.page-3):not(.page-4)');
        if (p1) p1.remove();
        const p2 = doc.querySelector('.page-2');
        if (p2) p2.remove();
        const p3 = doc.querySelector('.page-3');
        if (p3) p3.remove();
      }

      return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
    }

    if (action === 'get_pdf_blob' || action === 'get_contract_blob' || action === 'get_commitment_blob' || action === 'get_narsa_blob' || action === 'get_contract_arraybuffer' || action === 'get_commitment_arraybuffer' || action === 'get_narsa_arraybuffer') {
      const element = document.createElement('div');
      element.innerHTML = htmlContent;

      // Clean up print buttons
      const noPrint = element.querySelector('.no-print');
      if (noPrint) noPrint.remove();

      if (action === 'get_contract_blob' || action === 'get_contract_arraybuffer') {
        const p3 = element.querySelector('.page-3');
        if (p3) p3.remove();
        const p4 = element.querySelector('.page-4');
        if (p4) p4.remove();
      } else if (action === 'get_commitment_blob' || action === 'get_commitment_arraybuffer') {
        const p1 = element.querySelector('.contract:not(.page-2):not(.page-3):not(.page-4)');
        if (p1) p1.remove();
        const p2 = element.querySelector('.page-2');
        if (p2) p2.remove();
        const p4 = element.querySelector('.page-4');
        if (p4) p4.remove();
      } else if (action === 'get_narsa_blob' || action === 'get_narsa_arraybuffer') {
        const p1 = element.querySelector('.contract:not(.page-2):not(.page-3):not(.page-4)');
        if (p1) p1.remove();
        const p2 = element.querySelector('.page-2');
        if (p2) p2.remove();
        const p3 = element.querySelector('.page-3');
        if (p3) p3.remove();
      }

      // Render VISIBLE on top (not off-screen / negative z-index) — html2canvas
      // produces a BLANK PDF when its target is hidden behind the page, which
      // is the cause of the blank download (especially on mobile).
      element.style.position = 'fixed';
      element.style.left = '0';
      element.style.top = '0';
      element.style.zIndex = '2147483647';
      element.style.width = '210mm'; // A4 standard width
      element.style.background = '#ffffff';
      document.body.appendChild(element);
      try { window.scrollTo(0, 0); } catch (_) {}

      const filenameMap = {
        'get_pdf_blob': `Contrat_Complet_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        'get_contract_blob': `Contrat_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        'get_commitment_blob': `Engagement_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        'get_narsa_blob': `Contravention_NARSA_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        'get_contract_arraybuffer': `Contrat_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        'get_commitment_arraybuffer': `Engagement_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        'get_narsa_arraybuffer': `Contravention_NARSA_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`
      };

      const isArrayBuffer = action.endsWith('arraybuffer');
      const outputType = isArrayBuffer ? 'arraybuffer' : 'blob';

      const opt = {
        margin:       5,
        filename:     filenameMap[action],
        image:        { type: 'jpeg', quality: 0.98 },
        // windowWidth pins rendering to A4 width (≈794px @96dpi) so the layout
        // is identical on Desktop / Android / iPhone (no phone-width squashing).
        html2canvas:  { scale: 2, useCORS: true, logging: false, scrollY: 0, scrollX: 0, windowWidth: 794, backgroundColor: '#ffffff' },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
        // Each .contract is exactly one A4 page — clean breaks, no distortion.
        pagebreak:    { mode: ['css', 'legacy'], before: ['.page-2', '.page-3', '.page-4'] }
      };

      return new Promise((resolve, reject) => {
        html2pdf().set(opt).from(element).output(outputType).then(res => {
          document.body.removeChild(element);
          resolve(res);
        }).catch(err => {
          if (element.parentNode) document.body.removeChild(element);
          reject(err);
        });
      });
    } else if (action === 'download_pdf') {
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      element.style.position = 'fixed';
      element.style.left = '0';
      element.style.top = '0';
      element.style.zIndex = '2147483647';
      element.style.width = '210mm'; // A4 standard width
      element.style.background = '#ffffff';
      document.body.appendChild(element);
      try { window.scrollTo(0, 0); } catch (_) {}

      const opt = {
        margin:       5,
        filename:     `Contrat_${(r.clientName || 'Client').replace(/\s+/g, '_')}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, logging: false, scrollY: 0, scrollX: 0, windowWidth: 794, backgroundColor: '#ffffff' },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak:    { mode: ['css', 'legacy'], before: ['.page-2', '.page-3', '.page-4'] }
      };
      
      try {
        await html2pdf().set(opt).from(element).save();
      } catch (pdfErr) {
        console.error('html2pdf generation failed, falling back to HTML download', pdfErr);
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `Contrat_${(r.clientName || 'Client').replace(/\s+/g, '_')}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      } finally {
        if (element.parentNode) {
          document.body.removeChild(element);
        }
      }
    } else if (action === 'print_rectoverso') {
      // ── Contrat de Location — RECTO/VERSO print preview ──
      // Keep ONLY page 1 (recto) and page 2 (verso) so the contract prints as a
      // clean 2-sided document. Shows a preview with RECTO/VERSO labels and a
      // duplex tip; the user verifies the separation, then prints.
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // Remove the التزام (page 3) and NARSA (page 4) — not part of the recto/verso.
      doc.querySelector('.page-3')?.remove();
      doc.querySelector('.page-4')?.remove();
      // Remove the auto-print script so it does NOT print before the user reviews.
      doc.querySelectorAll('script').forEach(s => s.remove());

      const p1 = doc.querySelector('.contract:not(.page-2)');
      const p2 = doc.querySelector('.page-2');
      // On-screen RECTO / VERSO labels (hidden when actually printing).
      if (p1) p1.insertAdjacentHTML('beforebegin', `<div class="rv-label rv-recto">${isAr ? '🟦 الوجه الأمامي (RECTO) — الصفحة 1' : '🟦 RECTO — Face avant (page 1)'}</div>`);
      if (p2) p2.insertAdjacentHTML('beforebegin', `<div class="rv-label rv-verso">${isAr ? '🟩 الوجه الخلفي (VERSO) — الصفحة 2' : '🟩 VERSO — Face arrière (page 2)'}</div>`);

      // Replace the simple toolbar with a richer preview header + duplex tip.
      const tb = doc.querySelector('.no-print');
      if (tb) {
        tb.innerHTML = `
          <div class="rv-toolbar">
            <div class="rv-title">${isAr ? '👁️ معاينة الطباعة — وجهان (Recto-Verso)' : '👁️ Aperçu avant impression — Recto-Verso'}</div>
            <div class="rv-tip">${isAr
              ? 'في نافذة الطباعة فعّل « طباعة على الوجهين / Recto-Verso » واختر « قلب على الحافة الطويلة ».'
              : 'Dans la fenêtre d\'impression, activez « Recto-Verso (double face) » et choisissez « Retourner sur les bords longs ».'}</div>
            <button onclick="window.print()">${isAr ? '🖨️ طباعة وجهان' : '🖨️ Imprimer (Recto-Verso)'}</button>
          </div>`;
      }

      // Preview + print styling for the labels and clean duplex breaks.
      const style = doc.createElement('style');
      style.textContent = `
        .rv-toolbar { display:flex; flex-direction:column; gap:6px; align-items:center; }
        .rv-title { font-weight:800; font-size:16px; color:#0f172a; }
        .rv-tip { font-size:12px; color:#475569; max-width:560px; text-align:center; }
        .rv-label { max-width:200mm; margin:10px auto 2px; padding:5px 12px; border-radius:8px; font-weight:800; font-size:13px; color:#fff; }
        .rv-recto { background:#2563eb; }
        .rv-verso { background:#16a34a; }
        @media print {
          .rv-label { display:none !important; }
          /* Force the verso onto a new sheet so duplex sides align. */
          .contract.page-2 { page-break-before: always; break-before: page; }
        }`;
      doc.head.appendChild(style);

      const win = printWin || window.open('', '_blank');
      if (win) {
        win.document.write('<!DOCTYPE html>\n' + doc.documentElement.outerHTML);
        win.document.close();
      } else {
        alert(isAr ? '⚠️ مُنع فتح النافذة. اسمح بالنوافذ المنبثقة.' : '⚠️ Fenêtre bloquée. Autorisez les pop-ups.');
      }
    } else {
      const win = printWin || window.open('', '_blank');
      if (win) {
        win.document.write(htmlContent);
        win.document.close();
      } else {
        alert(isAr ? '⚠️ مُنع فتح النافذة. اسمح بالنوافذ المنبثقة.' : '⚠️ Fenêtre bloquée. Autorisez les pop-ups.');
      }
    }
};
